import { PlusCircleOutlined } from "@ant-design/icons";
import { Button, Form, Input, Row, Space, Table, TableProps, Typography, message } from "antd";
import { useEffect, useState } from "react";
import { BsPencilSquare, BsTrashFill } from "react-icons/bs";
import VocsModal from "src/components/Modal";
import { Filter, SettingChemicalProp, initChemProp } from "src/entity/settings/terminal-market";
import { createChemProperty, deleteChemProperty, getChemProperty, getMasterData, updateChemProperty } from "src/services/settings.service";
import { settingStore } from "src/stores/setting-store";
import { terminalStore } from "src/stores/terminal-store";
import { componentPalette } from "src/theme/component-palette";
import { useShallow } from "zustand/react/shallow";
import ChemicalPropertyModal from "./ChemicalPropertyModal";
import { Option } from "src/entity/settings/masterdata";
import { CommonError, CommonSuccess } from "src/entity/common/menus-enum.enum";
import axios from "axios";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { canEditTable } from "../PermissionCheck/PermissionCheck";
import { fillZeroByKeyList, formatNumberWithFillZero, parseAndFormatScientificNumber } from "src/utils/process-number";
import { calculateReportVocEmissionMasterChange } from "src/services/terminal-marketing.service";

type OnChange = NonNullable<TableProps<any>["onChange"]>;
type Filters = Parameters<OnChange>[1];

const ChemicalProperty = () => {
  const emission = settingStore(useShallow((state) => state.emission));
  const [emissionMaster, setEmissionMaster] = useState<Option[] | null>(null);
  const getEmission = settingStore(useShallow((state) => state.getEmission));
  const { permissions } = useNavigationMenu();

  const chemicalProperty = terminalStore(useShallow((state) => state.chemicalProperty));
  const totalChemProp = terminalStore(useShallow((state) => state.totalChemProp));
  const getAllChemicalProperty = terminalStore(useShallow((state) => state.getChemicalProperty));

  const filterChemProperty = terminalStore(useShallow((state) => state.filterChemProperty));
  const getFilterChemicalProperty = terminalStore(useShallow((state) => state.getFilterChemicalProperty));

  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState(1);
  const pageSize = 10;
  const [mode, setMode] = useState<"insert" | "update">("insert");
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState<Filters>({});
  const [form] = Form.useForm();
  const [chemicalId, setChemicalId] = useState<string>("");
  const [messageApi, contextHolder] = message.useMessage();
  const [isDuplicate, setIsDuplicate] = useState<boolean>(false);

  const onOpenModal = (mode: "insert" | "update", data: SettingChemicalProp) => {
    // console.log("onOpenModal", data);
    setMode(mode);
    setShowModal(true);
    setChemicalId(data._id);
    form.setFieldsValue({
      ...fillZeroByKeyList(data, {
        molecularWeight: 2,
        density: 4,
        temperature: 2,
        vaporPressure: 4,
      }),
    });
  };

  const onSave = async (mode: "insert" | "update", data: SettingChemicalProp) => {
    try {
      let response;
      setLoading(true);
      if (mode == "update") {
        if (chemicalId) {
          data = {
            ...data,
            _id: chemicalId,
          };
          response = await updateChemProperty(data);
        }
      } else {
        response = await createChemProperty(data);
      }
      if (response?.data.code === 200) {
        setLoading(false);
        setShowModal(false);
        fetchChemicalProperty();
        getFilterChemicalProperty();
        messageApi.open({
          type: "success",
          content: CommonSuccess.SAVED_SUCCESSFULL,
        });
      } else {
        setIsDuplicate(false);
        messageApi.error({
          type: "error",
          content: CommonError.SOMETHING_WRONG,
        });
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error?.response?.status === 409) {
          setIsDuplicate(true);
        } else {
          setIsDuplicate(false);
        }
      }
    } finally {
      setLoading(false);
      processCalculateReport(chemicalId);
    }
  };

  const processCalculateReport = async (chemicalId: string) => {
    try {
      setLoading(true);
      await calculateReportVocEmissionMasterChange({
        chemicalId: chemicalId,
        loadingById: "",
        emissionControlId: "",
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const showDeleteConfirm = async (value: SettingChemicalProp) => {
    const { _id } = value;
    VocsModal.delete({
      onOk: async () => {
        if (_id) {
          handleDelete(_id);
        }
      },
    });
  };

  const handleDelete = async (_id: string) => {
    try {
      setLoading(true);
      const response = await deleteChemProperty(_id);
      if (response.data.code === 200) {
        setLoading(false);
        messageApi.open({
          type: "success",
          content: CommonSuccess.DELETE_SUCCESSFULL,
        });
        fetchChemicalProperty();
      } else {
        setLoading(false);
        messageApi.open({
          type: "error",
          content: CommonError.SOMETHING_WRONG,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const mapFilter = (key: string): Filter[] | [] => {
    const resultFilter = filterChemProperty.find((item) => item.key === key)?.filter;
    if (resultFilter) {
      const filter: Filter[] = resultFilter?.map((value: any) => {
        let text = value;
        return {
          text,
          value: value,
        };
      });
      return filter;
    } else {
      return [];
    }
  };

  const onCloseModal = () => {
    setShowModal(false);
    setIsDuplicate(false);
    const formValue = form.getFieldsValue();
    if (formValue) {
      form.setFields(
        Object.keys(formValue).map((field) => ({
          name: field,
          errors: [],
        })),
      );
      form.resetFields();
    }
  };

  const onTableChange: TableProps<SettingChemicalProp>["onChange"] = (pagination: any, filters: any) => {
    let current = pagination.current;
    setFilter(filters);
    setCurrent(current);
  };

  const calPagination = (current?: number) => {
    if (current) {
      return (current - 1) * 10;
    }
  };

  const createParamFilter = () => {
    let queryParam = "";
    let offset = 0;
    let limit = pageSize;

    if (current) {
      let resultCalOffset = calPagination(current);
      if (resultCalOffset) {
        offset = resultCalOffset;
      }
      queryParam += `&offset=${offset}`;
    }
    if (limit) {
      queryParam += `&limit=${limit}`;
    }

    if (filter) {
      const str = Object.entries(filter)
        .filter(([, value]) => value)
        .map(([key, value]) => `&${key}=[${value?.join(",")}]`)
        .join("");
      queryParam += str;
    }

    return queryParam;
  };

  const fetchChemicalProperty = async () => {
    const query = createParamFilter();
    setLoading(true);
    await getAllChemicalProperty(query);
    setLoading(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await fetchChemicalProperty();
      await getFilterChemicalProperty();
      await getEmission({ type: "global", label: "Emission" });
      setLoading(false);
      if (emission[0] && emission[0].options) {
        setEmissionMaster(emission[0].options);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    fetchChemicalProperty();
  }, [current, filter]);

  const columns: TableProps<SettingChemicalProp>["columns"] = [
    {
      title: "No.",
      align: "center",
      render: (_, __, index) => <div>{index + 1 + (current - 1) * pageSize}</div>,
    },
    {
      title: "Chemical Name",
      dataIndex: "chemicalName",
      width: 140,
      filters: mapFilter("chemicalName"),
    },
    {
      title: "MW (lb/lb.mol)",
      dataIndex: "molecularWeight",
      filters: mapFilter("molecularWeight"),
      render: (value) => <div>{formatNumberWithFillZero(value, 2)}</div>,
    },
    {
      title: "Density (lb/gal)",
      dataIndex: "density",
      filters: mapFilter("density"),
      render: (value) => <div>{formatNumberWithFillZero(value, 4)}</div>,
    },
    {
      title: "Temp (℃)",
      dataIndex: "temperature",
      filters: mapFilter("temperature"),
      render: (value) => <div>{formatNumberWithFillZero(value, 2)}</div>,
    },
    {
      title: "VP (lb/in²)",
      dataIndex: "vaporPressure",
      filters: mapFilter("vaporPressure"),
      render: (value) => <div>{formatNumberWithFillZero(value, 4)}</div>,
    },
    {
      title: "Emission (%)",
      dataIndex: "emission",
      children: emissionMaster?.map((item) => ({
        title: item.label,
        dataIndex: "emission",
        width: "6%",
        render: (value) => <div>{value[item.value]}</div>,
      })),
    },
    {
      title: "Remark",
      dataIndex: "remark",
      key: "",
    },
    {
      title: "Action",
      key: "action",
      render: (value, record) => (
        <Space>
          <Button
            className="border-transparent bg-transparent"
            size={"small"}
            onClick={() => {
              onOpenModal("update", record);
            }}
            disabled={!canEditTable(permissions)}
          >
            <BsPencilSquare size={16} color={componentPalette.blueGrey} />
          </Button>
          <Button
            className="border-transparent bg-transparent"
            onClick={() => showDeleteConfirm(value)}
            disabled={!canEditTable(permissions)}
          >
            <BsTrashFill size={16} color={componentPalette.blueGrey} />
          </Button>
        </Space>
      ),
      align: "center",
    },
  ];

  return (
    <>
      {contextHolder}
      {emissionMaster && (
        <ChemicalPropertyModal
          form={form}
          open={showModal}
          mode={mode}
          loading={loading}
          isDuplicate={isDuplicate}
          onDuplicateChange={(isDup) => {
            setIsDuplicate(isDup);
          }}
          onCloseModal={onCloseModal}
          emissionMaster={emissionMaster}
          onSave={(mode, data) => onSave(mode, data)}
        />
      )}

      {canEditTable(permissions) && (
        <div style={{ display: "flex", justifyContent: "end", paddingBottom: "10px" }}>
          <Button
            type="primary"
            size="middle"
            onClick={() => onOpenModal("insert", initChemProp)}
            style={{
              display: "flex",
              alignItems: "center",
            }}
            disabled={!canEditTable(permissions)}
          >
            <PlusCircleOutlined />
            New
          </Button>
        </div>
      )}

      <Table
        className="w-full"
        columns={columns}
        dataSource={chemicalProperty}
        loading={loading}
        onChange={onTableChange}
        scroll={{ x: "max-content" }}
        pagination={{
          showSizeChanger: false,
          size: "small",
          current,
          pageSize,
          total: totalChemProp,
        }}
      />
    </>
  );
};

export default ChemicalProperty;
